import React, { useMemo } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { useStyles } from './DropdownMenuFilter.styles';
import CloseIcon from '@material-ui/icons/Close';
import Checkbox from '../Checkbox.component';
import { PopoverOrigin } from '@material-ui/core';

export interface MenuItemWithImage<T> {
  text: string;
  value?: T;
  imagePath?: string;
  jsxElement?: JSX.Element;
}

export interface FinalMenuItemWithImage<T> {
  text: string;
  value: T;
  imagePath?: string;
  jsxElement?: JSX.Element;
}

interface DropdownMenuProps<T> {
  anchorEl: SVGSVGElement | HTMLElement | null;
  setAnchorEl: (closeElement: SVGSVGElement | HTMLElement | null) => void;
  menuItems?: string[];
  menuItemsWithValue?: [string, T][];
  menuItemsWithImages?: MenuItemWithImage<T>[];
  hasCheckbox: boolean;
  checkedItems: T[];
  onChange: (item: T) => void;
  uncheckAllMenuItems: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

const DropdownMenuFilter = <T,>({
  menuItems,
  menuItemsWithValue,
  menuItemsWithImages,
  ...props
}: DropdownMenuProps<T>): JSX.Element => {
  const classes = useStyles();

  const finalMenuItems: [string, T][] = useMemo(() => {
    if (menuItemsWithValue) {
      return menuItemsWithValue;
    }
    return (menuItems ?? []).map((item: string | [string, T]) => {
      if (!Array.isArray(item)) {
        return [item, item as unknown as T];
      } else return item;
    });
  }, [menuItems, menuItemsWithValue]);

  const finalMenuItemsWithImages: FinalMenuItemWithImage<T>[] = useMemo(() => {
    return (menuItemsWithImages ?? []).map((item: MenuItemWithImage<T>) => {
      if (item.value === undefined) {
        return { ...item, value: item.text as unknown as T };
      } else return { ...item, value: item.value as unknown as T };
    });
  }, [menuItemsWithImages]);

  const handleCloseMenu = () => {
    props.setAnchorEl(null);
  };
  return (
    <Menu
      className={classes.dropdownMenuFilter}
      id="dropdown-menu"
      anchorEl={props.anchorEl}
      keepMounted
      open={Boolean(props.anchorEl)}
      onClose={handleCloseMenu}
      anchorOrigin={props.anchorOrigin}
      transformOrigin={props.transformOrigin}
      aria-controls="menu"
    >
      {finalMenuItems.length > 0 &&
        finalMenuItems.map((item: [string, T]) => {
          const label: string = item[0];
          const value: T = item[1];
          if (item[0] == 'hr')
            // for adjustments
            return (
              <div key={`filter-sub-${item[1]}`}>
                <hr className={classes.hr} />
                <h5 className={classes.menuSubheader}>{item[1]}</h5>
              </div>
            );
          return (
            <MenuItem
              disableRipple
              key={label}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  props.onChange(value);
                }
              }}
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.preventDefault();
                props.onChange(value);
              }}
              aria-controls="menu"
              aria-checked={props.checkedItems.indexOf(value) !== -1}
              role="menuitemradio"
            >
              {props.hasCheckbox && (
                <Checkbox
                  className={classes.filterMenuCheckbox}
                  checked={props.checkedItems.indexOf(value) !== -1}
                  onChange={() => props.onChange(value)}
                />
              )}
              {item[0]}
            </MenuItem>
          );
        })}
      {finalMenuItemsWithImages.length > 0 &&
        finalMenuItemsWithImages.map(({ text, value, imagePath, jsxElement }) => (
          <MenuItem
            disableRipple
            key={text}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter') {
                props.onChange(value);
              }
            }}
            onClick={(event) => {
              event.preventDefault();
              props.onChange(value);
            }}
          >
            {props.hasCheckbox && (
              <Checkbox
                className={classes.filterMenuCheckbox}
                checked={props.checkedItems.indexOf(value) !== -1}
                onChange={() => props.onChange(value)}
              />
            )}
            {imagePath && <img className={classes.icon} src={imagePath} alt={text} />}
            {jsxElement && <span style={{ marginRight: 5 }}>{jsxElement}</span>}
            {text}
          </MenuItem>
        ))}

      <hr className={classes.hr} />
      <MenuItem
        disableRipple
        className={classes.filterCloseText}
        //uncheckAllMenuItems needs to be fired with empty parameters
        onClick={() => props.uncheckAllMenuItems()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            props.uncheckAllMenuItems();
          }
        }}
      >
        <CloseIcon className={classes.filterListCloseIcon} />
        Wyczyść
      </MenuItem>
    </Menu>
  );
};

export default DropdownMenuFilter;
